import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardListIcon, FileTextIcon, PhoneIcon, CalendarIcon, ZapIcon, TargetIcon, LockIcon, BarChart3Icon, HandshakeIcon, TrendingUpIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { ScrollingTicker } from '../components/ScrollingTicker';
import { Link } from 'react-router-dom';
interface HomeProps {
  onBuyerContactOpen: () => void;
  onOwnerContactOpen: () => void;
}
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.15
    }
  },
  viewport: {
    once: true
  }
};
const tickerItems = ['30–45 qualified seller intros / 90 days', '~12% conversion efficiency vs ~1% traditional', '$5–10M EBITDA buy box focus', 'Monthly billing, defined outputs', 'Off-market first conversations'];
const buyerFaqItems = [{
  question: 'What is a "qualified introduction"?',
  answer: 'Every introduction represents a business owner who has been personally contacted, screened against your specific buy box criteria, and has confirmed genuine interest in exploring a conversation with a qualified buyer. These are not names on a list or scraped contacts. Each owner has spoken directly with our team, confirmed key details — approximate revenue and EBITDA range, reason for exploring, timeline, and willingness to engage — and has been graded for fit. You receive a structured deal brief with each introduction so your team can make a fast, informed go/no-go decision.'
}, {
  question: "What's included in each deal brief?",
  answer: "Each deal brief is a one-page screening summary designed for fast IC-level triage. It includes: revenue and EBITDA ranges (as stated and validated in conversation), owner's stated motivation and timeline, transition posture and owner involvement expectations, customer concentration snapshot, key risks and flags we identified during screening, full call notes and recording, and our next-step recommendation graded A (advance immediately), B (qualify further), or C (park/monitor). Your team gets everything needed for a 5-minute decision — no extra research required."
}, {
  question: 'How do you define the buy box?',
  answer: 'Before we start, we align on your specific parameters: target EBITDA range (our core capability is $5–10M), geographic preferences (US-wide by default), sector focus or explicit exclusions (we exclude Tech and Oil & Gas by default), deal structure preferences (control, majority, minority), and any other thesis-specific criteria. We only deliver introductions that fall within these parameters. If an owner is marginally outside your box — say $4.5M EBITDA with strong growth — we flag it clearly and let your team decide.'
}, {
  question: 'How does the pilot work?',
  answer: 'The pilot is a 90-day engagement with defined, measurable outputs. You invest $60,000/month, billed in advance ($180,000 total). Month 1 delivers 8–12 qualified introductions as the campaign launches and the initial seller pipeline is established. Month 2 delivers 10–15 introductions as pipeline momentum builds. Month 3 delivers 12–18 introductions at full run-rate. Expected outcomes based on track record: 30–45 total introductions, 3–5 actionable deals, 1–2 opportunities progressing to IOI/LOI stage within the pilot window.'
}, {
  question: 'We already have an internal sourcing team.',
  answer: "Good — this augments them. Your internal team likely has limited bandwidth for cold outreach to the $5–10M EBITDA segment. We add a dedicated, parallel sourcing channel that generates introductions your internal team can advance. Most clients see us as a force multiplier, not a replacement. The introductions we deliver are opportunities your team wouldn't have found — or wouldn't have found fast enough."
}, {
  question: '$60K/month feels expensive for sourcing.',
  answer: 'Consider the alternative math: a senior deal professional costs $300K–$500K/year fully loaded and generates maybe 2–3 proprietary deals per year. At $60K/month, we deliver 30–45 qualified introductions in 90 days with ~3–5 becoming actionable. On a cost-per-qualified-opportunity basis, this is dramatically more efficient than internal sourcing — and it starts producing in weeks, not quarters. If even one deal closes from the pilot, the ROI is measured in multiples, not percentages.'
}, {
  question: 'How do I know the introductions will be quality?',
  answer: "Two ways: First, ask for our sample deal brief — it shows exactly the level of detail and screening rigor you'll receive with every introduction. Second, the pilot structure itself protects you. Monthly billing means you see results before you pay for the next month. If the quality doesn't meet your standards in Month 1, you have a direct conversation with us — and if we can't fix it, you stop."
}, {
  question: 'What if no deals close from the pilot?',
  answer: "Deal velocity in the lower-middle market is typically 6–12 months from first introduction to close. The pilot creates a pipeline — it doesn't guarantee a close within 90 days (and anyone promising that isn't being honest). What it does guarantee: 30–45 qualified introductions with real, screened business owners. The pipeline you build during the pilot continues producing results for months after it ends."
}];
const ownerFaqItems = [{
  question: 'Is this really confidential?',
  answer: "Yes — completely. Your business is never listed publicly. We don't share your name, your business name, or any identifying information with any buyer without your explicit consent. Initial conversations happen in general terms — industry, revenue range, geography — until you're ready for a direct introduction. You control every step of what gets shared and with whom."
}, {
  question: 'Do I have to commit to selling?',
  answer: "No. The entire point of our process is to give you information and optionality before you make any decisions. Many owners go through our process and decide the timing isn't right — and that's perfectly fine. You might discover your business is worth more than you thought. You might find a buyer who changes your mind. Or you might simply decide to wait. All of those outcomes are valid, and none of them cost you anything."
}, {
  question: 'What kind of buyers will I be speaking with?',
  answer: 'PE groups, family offices, independent sponsors, and well-capitalized operators who are actively acquiring businesses in your size range. These are not first-time buyers or SBA-dependent acquirers. Every buyer in our network has been vetted for financial capability, acquisition track record, and deal structure flexibility. You will only speak with buyers who have been pre-screened and who have confirmed interest in a business matching your profile.'
}, {
  question: 'What does the process cost me?',
  answer: 'Nothing. Our service is paid for entirely by the buyer side. You will never receive a bill, owe a fee, or face any financial obligation at any stage of the process — including if you decide not to proceed.'
}, {
  question: 'How long does this take?',
  answer: "That depends entirely on you. If you're actively exploring, we can typically coordinate a first qualified buyer conversation within 2–3 weeks of your intake call. If you want to move slowly and simply stay informed about what's possible, we can accommodate that timeline as well. There is no deadline, no pressure, and no expiration on your options."
}];
export function Home({
  onBuyerContactOpen,
  onOwnerContactOpen
}: HomeProps) {
  const [activeTab, setActiveTab] = useState<'buyers' | 'owners'>('buyers');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const currentFaqItems = activeTab === 'buyers' ? buyerFaqItems : ownerFaqItems;
  return <main className="bg-deal-black" data-id="element-395">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-id="element-396">
        <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-397" />
        <div className="absolute inset-0 bg-gradient-to-b from-deal-black/20 via-transparent to-deal-black" data-id="element-398" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-32 pb-20" data-id="element-399">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-400">
            <span className="inline-block text-deal-green text-[11px] font-semibold tracking-[0.25em] uppercase mb-8 border border-deal-green/30 px-4 py-1.5" data-id="element-401">
              Off-Market Deal Sourcing
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="text-5xl md:text-6xl lg:text-[80px] font-bold text-white leading-[1.05] tracking-tight mb-8" data-id="element-402">
            Proprietary Deal Flow,{' '}
            <span className="text-deal-green" data-id="element-403">Delivered.</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto mb-12 leading-relaxed font-light" data-id="element-404">
            We connect qualified business owners with serious buyers through
            direct, off-market conversations — without auctions, noise, or
            wasted time.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-405">
            <button onClick={onBuyerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2" data-id="element-406">
              I'm a Buyer
              <ArrowRightIcon className="w-4 h-4" data-id="element-407" />
            </button>
            <button onClick={onOwnerContactOpen} className="px-8 py-4 border-2 border-dashed border-deal-green text-deal-green text-sm font-bold tracking-widest uppercase hover:bg-deal-green/10 transition-colors duration-300" data-id="element-408">
              I'm a Business Owner
            </button>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2,
        duration: 0.6
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2" data-id="element-409">
          <div className="w-px h-16 bg-gradient-to-b from-white/20 to-transparent mx-auto" data-id="element-410" />
        </motion.div>
      </section>

      {/* Scrolling Ticker */}
      <ScrollingTicker items={tickerItems} data-id="element-411" />

      {/* The Problem Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-412">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-413">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-414">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-415">
              The Problem
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-416">
              The current model is broken — for everyone.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-417">
              Whether you're buying or selling, the traditional market forces
              you into processes that waste time, destroy value, and strip away
              control.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/[0.06]" data-id="element-418">
            {/* For Buyers Column */}
            <motion.div {...fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12" data-id="element-419">
              <p className="text-deal-green text-[11px] tracking-[0.2em] uppercase font-semibold mb-4" data-id="element-420">
                For Buyers (PE / Family Offices)
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-421">
                The visible market is working against you.
              </h3>
              <p className="text-white/50 leading-relaxed mb-6" data-id="element-422">
                Every deal on the market is seen by dozens of firms. By the time
                it hits your desk, you're competing against 15+ bidders —
                driving multiples up and returns down.
              </p>
              <ul className="space-y-3" data-id="element-423">
                <li className="flex items-start gap-3" data-id="element-424">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-425" />
                  <span className="text-white/50 text-sm" data-id="element-426">
                    The visible market is crowded — the same deals circulate to
                    every buyer
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-427">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-428" />
                  <span className="text-white/50 text-sm" data-id="element-429">
                    Auctions compress returns and commoditize your capital
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-430">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-431" />
                  <span className="text-white/50 text-sm" data-id="element-432">
                    Proprietary sourcing burns senior time with minimal yield
                    (~1% close rate)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-433">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-434" />
                  <span className="text-white/50 text-sm" data-id="element-435">
                    Your team spends 80% of their effort filtering noise, not
                    advancing real opportunities
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-436">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-437" />
                  <span className="text-white/50 text-sm" data-id="element-438">
                    Add-on and platform searches stall without consistent deal
                    flow
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* For Owners Column */}
            <motion.div {...fadeInUp} transition={{
            delay: 0.1
          }} className="bg-[#0d0d0d] p-10 md:p-12" data-id="element-439">
              <p className="text-deal-green text-[11px] tracking-[0.2em] uppercase font-semibold mb-4" data-id="element-440">
                For Business Owners
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-441">
                You've built something valuable — but the path forward feels
                uncertain.
              </h3>
              <p className="text-white/50 leading-relaxed mb-6" data-id="element-442">
                The idea of selling feels risky. Public listings, intrusive
                broker processes, and buyer parade fatigue keep you on the
                sidelines, even when you're ready.
              </p>
              <ul className="space-y-3" data-id="element-443">
                <li className="flex items-start gap-3" data-id="element-444">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-445" />
                  <span className="text-white/50 text-sm" data-id="element-446">
                    Selling feels risky and public — your employees, customers,
                    and competitors could find out
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-447">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-448" />
                  <span className="text-white/50 text-sm" data-id="element-449">
                    Broker processes are time-consuming, intrusive, and start
                    before you're ready
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-450">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-451" />
                  <span className="text-white/50 text-sm" data-id="element-452">
                    Most owners want optionality first — not a commitment to
                    sell
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-453">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-454" />
                  <span className="text-white/50 text-sm" data-id="element-455">
                    You want to talk to serious, qualified buyers — not
                    tire-kickers
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-456">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-457" />
                  <span className="text-white/50 text-sm" data-id="element-458">
                    You want control over who, when, and how
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Deliver Section */}
      <section className="py-28 md:py-36 bg-deal-black border-t border-white/[0.06]" data-id="element-459">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-460">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-461">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-462">
              What We Deliver
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-463">
              Outcomes, not software.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-464">
              We don't sell a platform or a database. We deliver real, qualified
              introductions between business owners and buyers — with structured
              context your team can act on immediately.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06]" data-id="element-465">
            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-466">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-467">
                <ClipboardListIcon className="w-5 h-5 text-deal-green" data-id="element-468" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-469">
                Qualified Seller Introductions
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-470">
                Off-market first conversations with screened, motivated business
                owners in your buy box
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-471">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-472">
                <FileTextIcon className="w-5 h-5 text-deal-green" data-id="element-473" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-474">
                Screening-Ready Summaries
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-475">
                Structured deal briefs with financials, motivation, risks, and
                our recommendation — ready for fast IC triage
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-476">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-477">
                <PhoneIcon className="w-5 h-5 text-deal-green" data-id="element-478" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-479">
                Call Context & Notes
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-480">
                Full call recordings and detailed notes so your team never hunts
                for context or backstory
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-481">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-482">
                <CalendarIcon className="w-5 h-5 text-deal-green" data-id="element-483" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-484">
                Repeatable Monthly Cadence
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-485">
                Predictable pipeline growth with defined monthly outputs — not
                sporadic, unpredictable leads
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-486">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-487">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-488">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-489">
              How It Works
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-490">
              Three steps. Zero complexity.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-491">
              We handle the sourcing, screening, and scheduling. Your team
              focuses on what they do best — evaluating and closing deals.
            </p>
          </motion.div>

          <div className="space-y-16" data-id="element-492">
            <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-493">
              <div className="md:col-span-1" data-id="element-494">
                <div className="w-12 h-12 rounded-full border border-deal-green/30 flex items-center justify-center text-deal-green text-lg font-bold" data-id="element-495">
                  01
                </div>
              </div>
              <div className="md:col-span-11" data-id="element-496">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-497">
                  We Identify & Qualify Owners
                </h3>
                <p className="text-white/50 leading-relaxed" data-id="element-498">
                  We proactively source and screen business owners in your
                  target size range, industry focus, and geography. Every owner
                  is personally contacted, qualified against your buy box
                  criteria, and confirmed as genuinely interested in exploring a
                  conversation.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-499">
              <div className="md:col-span-1" data-id="element-500">
                <div className="w-12 h-12 rounded-full border border-deal-green/30 flex items-center justify-center text-deal-green text-lg font-bold" data-id="element-501">
                  02
                </div>
              </div>
              <div className="md:col-span-11" data-id="element-502">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-503">
                  We Deliver Introductions
                </h3>
                <p className="text-white/50 leading-relaxed" data-id="element-504">
                  Each qualified introduction comes with a structured deal
                  brief: financial ranges, owner motivation and timeline,
                  customer concentration, key risks, call notes, and our
                  next-step recommendation. Your team gets everything needed for
                  a fast go/no-go decision.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-505">
              <div className="md:col-span-1" data-id="element-506">
                <div className="w-12 h-12 rounded-full border border-deal-green/30 flex items-center justify-center text-deal-green text-lg font-bold" data-id="element-507">
                  03
                </div>
              </div>
              <div className="md:col-span-11" data-id="element-508">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-509">
                  Your Team Advances the Fit
                </h3>
                <p className="text-white/50 leading-relaxed" data-id="element-510">
                  You advance the opportunities that fit your thesis. We handle
                  the top of funnel so your deal team spends their time on
                  qualified conversations — not cold outreach, list scrubbing,
                  or chasing dead leads.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Choose Your Path Section */}
      <section className="py-28 md:py-36 bg-deal-black border-t border-white/[0.06]" data-id="element-511">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-512">
          <motion.div {...fadeInUp} className="mb-16 text-center" data-id="element-513">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-514">
              Choose Your Path
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-515">
              Built for buyers. Built for owners.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/[0.06]" data-id="element-516">
            {/* For Buyers Card */}
            <motion.div {...fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12" data-id="element-517">
              <p className="text-deal-green text-[11px] tracking-[0.2em] uppercase font-semibold mb-4" data-id="element-518">
                For Buyers
              </p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-519">
                Expand proprietary deal flow in your buy box
              </h3>
              <p className="text-white/50 leading-relaxed mb-6" data-id="element-520">
                Get a predictable stream of off-market seller introductions with
                screening-ready context — so your team advances real
                opportunities, not noise.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-521">
                <li className="flex items-start gap-3" data-id="element-522">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-523" />
                  <span className="text-white/70 text-sm" data-id="element-524">
                    30–45 qualified introductions in 90 days
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-525">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-526" />
                  <span className="text-white/70 text-sm" data-id="element-527">
                    Structured deal briefs for fast IC screening
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-528">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-529" />
                  <span className="text-white/70 text-sm" data-id="element-530">
                    Dramatically less time wasted on weak deals
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-531">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-532" />
                  <span className="text-white/70 text-sm" data-id="element-533">
                    ~12% conversion efficiency vs ~1% traditional sourcing
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-534">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-535" />
                  <span className="text-white/70 text-sm" data-id="element-536">
                    Monthly billing, defined outputs, no long-term lock-in
                  </span>
                </li>
              </ul>
              <button onClick={onBuyerContactOpen} className="inline-flex items-center gap-2 px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300" data-id="element-537">
                Get Deal Flow
                <ArrowRightIcon className="w-4 h-4" data-id="element-538" />
              </button>
            </motion.div>

            {/* For Owners Card */}
            <motion.div {...fadeInUp} transition={{
            delay: 0.1
          }} className="bg-[#0d0d0d] p-10 md:p-12" data-id="element-539">
              <p className="text-deal-green text-[11px] tracking-[0.2em] uppercase font-semibold mb-4" data-id="element-540">
                For Owners
              </p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-541">
                Explore selling options — confidentially
              </h3>
              <p className="text-white/50 leading-relaxed mb-6" data-id="element-542">
                Talk to serious, qualified buyers without listing your business,
                committing to a sale, or giving up control of the process.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-543">
                <li className="flex items-start gap-3" data-id="element-544">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-545" />
                  <span className="text-white/70 text-sm" data-id="element-546">
                    No public listing required — ever
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-547">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-548" />
                  <span className="text-white/70 text-sm" data-id="element-549">
                    No obligation to proceed at any stage
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-550">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-551" />
                  <span className="text-white/70 text-sm" data-id="element-552">
                    Speak with serious, vetted buyers only
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-553">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-554" />
                  <span className="text-white/70 text-sm" data-id="element-555">
                    Flexible deal structures: majority, full exit, transitions
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-556">
                  <div className="w-1.5 h-1.5 rounded-full bg-deal-green flex-shrink-0 mt-2.5" data-id="element-557" />
                  <span className="text-white/70 text-sm" data-id="element-558">
                    You control who you speak with and when
                  </span>
                </li>
              </ul>
              <button onClick={onOwnerContactOpen} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-dashed border-deal-green text-deal-green text-sm font-bold tracking-widest uppercase hover:bg-deal-green/10 transition-colors duration-300" data-id="element-559">
                Explore Your Options
                <ArrowRightIcon className="w-4 h-4" data-id="element-560" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Deal Flow Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-561">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-562">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-563">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-564">
              Why Deal Flow
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-565">
              Built by operators. For dealmakers.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-3xl" data-id="element-566">
              We're not brokers. We're not a SaaS platform. We're a dedicated
              deal origination engine that does one thing exceptionally well:
              deliver qualified, off-market seller introductions to serious
              buyers — consistently, predictably, and with full context.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]" data-id="element-567">
            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-568">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-569">
                <ZapIcon className="w-5 h-5 text-deal-green" data-id="element-570" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-571">
                Speed to Value
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-572">
                First introductions begin within 2–3 weeks of engagement. No
                6-month ramp-up. No technology implementation. We start working
                your buy box immediately.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-573">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-574">
                <TargetIcon className="w-5 h-5 text-deal-green" data-id="element-575" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-576">
                Precision Targeting
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-577">
                Every introduction is manually qualified against your specific
                criteria. We don't batch-blast owners with form letters. We have
                real conversations and confirm fit before you ever see a name.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-578">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-579">
                <LockIcon className="w-5 h-5 text-deal-green" data-id="element-580" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-581">
                Complete Confidentiality
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-582">
                For both sides. Owners' identities are protected until they
                consent to a conversation. Buyers' strategies and criteria stay
                private. Trust is the foundation of every introduction we make.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-583">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-584">
                <BarChart3Icon className="w-5 h-5 text-deal-green" data-id="element-585" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-586">
                Defined Outputs
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-587">
                We don't sell effort or hours. We sell results: 30–45 qualified
                introductions in 90 days, with structured deal briefs and clear
                next-step recommendations for each one.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-588">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-589">
                <HandshakeIcon className="w-5 h-5 text-deal-green" data-id="element-590" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-591">
                Alignment by Design
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-592">
                Monthly billing. No long-term contracts. If the outputs don't
                justify the investment, you stop. That alignment keeps us
                accountable and keeps you in control.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-593">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-594">
                <TrendingUpIcon className="w-5 h-5 text-deal-green" data-id="element-595" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-596">
                Compounding Pipeline
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-597">
                Every month builds on the last. Relationships we start in month
                1 may convert in month 6. Your proprietary pipeline grows
                steadily — not sporadically.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-28 md:py-36 bg-deal-black border-t border-white/[0.06]" data-id="element-598">
        <div className="max-w-4xl mx-auto px-6 lg:px-10" data-id="element-599">
          <motion.div {...fadeInUp} className="mb-12 text-center" data-id="element-600">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-601">
              FAQ
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" data-id="element-602">
              Questions we hear most.
            </h2>
          </motion.div>

          {/* Tabs */}
          <div className="flex justify-center gap-8 mb-12" data-id="element-603">
            <button onClick={() => {
            setActiveTab('buyers');
            setOpenFaq(null);
          }} className={`text-sm font-semibold tracking-widest uppercase pb-2 border-b-2 transition-colors ${activeTab === 'buyers' ? 'text-deal-green border-deal-green' : 'text-white/40 border-transparent hover:text-white/60'}`} data-id="element-604">
              Buyer Questions
            </button>
            <button onClick={() => {
            setActiveTab('owners');
            setOpenFaq(null);
          }} className={`text-sm font-semibold tracking-widest uppercase pb-2 border-b-2 transition-colors ${activeTab === 'owners' ? 'text-deal-green border-deal-green' : 'text-white/40 border-transparent hover:text-white/60'}`} data-id="element-605">
              Owner Questions
            </button>
          </div>

          {/* Accordion */}
          <div className="divide-y divide-white/[0.06]" data-id="element-606">
            {currentFaqItems.map((faq, i) => <div key={`${activeTab}-${i}`} data-id="element-607">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between text-left py-6 focus:outline-none group" data-id="element-608">
                  <span className="text-lg font-medium text-white/80 group-hover:text-white transition-colors pr-8" data-id="element-609">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-white/30 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-610" />
                </button>
                <AnimatePresence data-id="element-611">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-612">
                      <p className="text-white/50 text-base leading-relaxed pb-8 pr-8 font-light" data-id="element-613">
                        {faq.answer}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d] relative overflow-hidden" data-id="element-614">
        <div className="absolute inset-0 bg-gradient-to-t from-deal-green/[0.04] to-transparent" data-id="element-615" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center" data-id="element-616">
          <motion.div {...fadeInUp} data-id="element-617">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-618">
              Get Started
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight" data-id="element-619">
              Ready to build your pipeline or explore your options?
            </h2>
            <div className="w-12 h-px bg-deal-green/40 mx-auto mb-10" data-id="element-620" />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-621">
              <button onClick={onBuyerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2" data-id="element-622">
                Start a Pilot
                <ArrowRightIcon className="w-4 h-4" data-id="element-623" />
              </button>
              <button onClick={onOwnerContactOpen} className="px-8 py-4 border-2 border-dashed border-deal-green text-deal-green text-sm font-bold tracking-widest uppercase hover:bg-deal-green/10 transition-colors duration-300" data-id="element-624">
                Explore as an Owner
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}